import { DateFormat } from '../shared/enums/date-formats.enum';

export function formatDate(dateString: string | Date, locale = 'pt-BR'): string {
  if (!dateString) return 'invalid date';

  let dateStr: string;
  let date: Date;

  try {
    if (typeof dateString === 'string') {
      date = new Date(dateString);
    } else {
      date = dateString;
    }
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return 'invalid date';
    }
    const dateStr = date.toISOString();
    if (typeof dateStr === 'string' && dateStr.length >= 10) {
      const ano = dateStr.substring(0, 4);
      const mes = dateStr.substring(5, 7);
      const dia = dateStr.substring(8, 10);
      return `${dia}/${mes}/${ano}`;
    } else {
      return 'invalid date';
    }
  } catch (e) {
    console.error('Error formatting date:', e);
    return 'invalid date';
  }
}

export function dateToUserTimezone(
  value: string | Date,
  format: DateFormat = DateFormat.DATETIME,
  locale = 'pt-BR',
): string {
  if (!value) {
    return 'invalid date';
  }

  try {
    const date = typeof value === 'string' ? new Date(value) : value;

    if (isNaN(date.getTime())) {
      return 'invalid date';
    }

    const options: Intl.DateTimeFormatOptions = {};

    switch (format) {
      case DateFormat.DATE:
        options.year = 'numeric';
        options.month = '2-digit';
        options.day = '2-digit';
        break;
      case DateFormat.TIME:
        options.hour = '2-digit';
        options.minute = '2-digit';
        options.second = '2-digit';
        options.hour12 = false;
        break;
      case DateFormat.DATETIME:
      default:
        options.year = 'numeric';
        options.month = '2-digit';
        options.day = '2-digit';
        options.hour = '2-digit';
        options.minute = '2-digit';
        options.second = '2-digit';
        options.hour12 = false;
        break;
    }

    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch (e) {
    console.error('Error formatting date:', e);
    return 'invalid date';
  }
}

export function substractDaysFromDate(date: Date, days: number): Date {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() - days);
  return newDate;
}

/**
 * Get month name in Portuguese (short format)
 * @param monthIndex Month index (0-11)
 * @returns Short month name in Portuguese
 */
export function getMonthName(monthIndex: number): string {
  const months = [
    'Jan',
    'Fev',
    'Mar',
    'Abr',
    'Mai',
    'Jun',
    'Jul',
    'Ago',
    'Set',
    'Out',
    'Nov',
    'Dez',
  ];
  return months[monthIndex] || 'Jan';
}
/**
 * Returns an ISO string with local offset (e.g. 2025-11-30T03:00:00-03:00)
 * @param date Date instance
 * @returns ISO string with local offset (e.g. 2025-11-30T03:00:00-03:00)
 */
export function toLocalISOStringWithOffset(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hour = pad(date.getHours());
  const min = pad(date.getMinutes());
  const sec = pad(date.getSeconds());
  const ms = date.getMilliseconds().toString().padStart(3, '0');
  const offset = -date.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const absOffset = Math.abs(offset);
  const offsetHour = pad(Math.floor(absOffset / 60));
  const offsetMin = pad(absOffset % 60);
  return `${year}-${month}-${day}T${hour}:${min}:${sec}.${ms}${sign}${offsetHour}:${offsetMin}`;
}

/**
 * Returns an ISO string with local offset for the start of the day (00:00:00.000)
 * preserving the year/month/day of the given date.
 * Ex. (Brasil, -03:00): 01/01/2024 -> "2024-01-01T00:00:00.000-03:00".
 * @param date Date instance (or undefined)
 * @returns ISO string with local offset, or undefined when no date is provided
 */
export function toStartOfDayLocalISO(date?: Date | null): string | undefined {
  if (!date) return undefined;
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return toLocalISOStringWithOffset(start);
}

/**
 * Returns an ISO string with local offset for the end of the day (23:59:59.999)
 * preserving the year/month/day of the given date.
 * Ex. (Brasil, -03:00): 31/12/2024 -> "2024-12-31T23:59:59.999-03:00".
 * @param date Date instance (or undefined)
 * @returns ISO string with local offset, or undefined when no date is provided
 */
export function toEndOfDayLocalISO(date?: Date | null): string | undefined {
  if (!date) return undefined;
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  return toLocalISOStringWithOffset(end);
}
